import React, { useState } from "react";
import {
  MultiSelectFilter,
  SelectOption,
  StringArrayFilterValue,
} from "../../types/filter";
import { FixaListView } from "../../../wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "../../../wrappers/FixaListView/FixaListViewItem";
import FixaSearch from "../../../wrappers/FixaSearch/FixaSearch";
import { useTranslation } from "@coworker/locales";
import SelectListViewCss from "./SelectListView.module.css";
import { localeIncludes } from "./SelectListView.helper";
import {
  getSelectOptionKey,
  isSelectGroup,
  isSelectOption,
  isSelectOptionSelected,
} from "../../utils/filter/selectoption";
import { CategoryListItem } from "./CategoryListItem";
import { GroupMultiSelectListItem } from "./GroupMultiSelectListItem";
import FixaInlineMessage from "../../../wrappers/FixaInlineMessage/FixaInlineMessage";
import InformationCircleIconPath from "../../../wrappers/FixaSSRIcon/paths/InformationCircleIconPath";
import { FixaSkeleton } from "../../../wrappers/FixaSkeleton/FixaSkeleton";

interface MultiSelectListViewProps {
  prefix: string;
  filter: MultiSelectFilter;
  selected: StringArrayFilterValue;
  onChange: (option: SelectOption, isSelected: boolean) => void;
}

export const MultiSelectListView = ({
  prefix,
  filter,
  onChange,
  selected,
}: MultiSelectListViewProps) => {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const {
    id,
    title,
    options,
    showSearch,
    noResultsTitle,
    noResultsDescription,
    isFetching,
  } = filter;

  if (isFetching)
    return (
      <div className={SelectListViewCss["loading"]}>
        <FixaSkeleton height="32px" />
      </div>
    );

  const showNoResults =
    !options.length && noResultsTitle && noResultsDescription && !isFetching;

  if (showNoResults)
    return (
      <div className={SelectListViewCss["no-results"]}>
        <FixaInlineMessage
          className={SelectListViewCss["no-results-text"] as string}
          title={noResultsTitle}
          body={noResultsDescription}
          ssrIcon={InformationCircleIconPath}
          variant={"cautionary"}
        />
      </div>
    );

  return (
    <div>
      {showSearch && (
        <FixaSearch
          className={SelectListViewCss["search-input"] ?? ""}
          id={`listview-search-${prefix}-${id}`}
          value={searchInput}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchInput(e.target.value);
          }}
          onClear={() => setSearchInput("")}
          placeholder={t("searchFilterOptionsString", { title })}
          data-testid={`listview-search-${prefix}-${id}`}
        />
      )}
      <FixaListView
        id={`listview-${prefix}-${id}`}
        data-testid={`listview-${prefix}-${id}`}
        size="small"
        showDivider={false}
      >
        {options
          .filter(
            (option) =>
              !isSelectOption(option) ||
              isSelectOptionSelected(selected, option) ||
              localeIncludes(option.title, searchInput)
          )
          .map((option, idx) => {
            if (isSelectGroup(option)) {
              return (
                <GroupMultiSelectListItem
                  key={getSelectOptionKey(idx, option)}
                  item={option}
                  prefix={prefix}
                  selected={selected}
                  searchInput={searchInput}
                  onChange={onChange}
                  defaultValue={filter.defaultValue as StringArrayFilterValue}
                />
              );
            } else if (isSelectOption(option)) {
              const { value } = option;
              const isSelected = isSelectOptionSelected(selected, option);
              return (
                <FixaListViewItem
                  key={getSelectOptionKey(idx, option)}
                  inset
                  onChange={() => {
                    onChange(option, isSelected);
                  }}
                  control="checkbox"
                  controlProps={{
                    value: isSelected ? "ON" : "OFF",
                    checked: isSelected,
                  }}
                  showDivider={false}
                  title={option.title}
                  data-testid={`listview-option-${prefix}-${value}`}
                />
              );
            } else {
              return (
                <CategoryListItem
                  key={getSelectOptionKey(idx, option)}
                  item={option}
                  prefix={prefix}
                />
              );
            }
          })}
      </FixaListView>
    </div>
  );
};
